import React, { useEffect, useState } from 'react'
import 'bootstrap/dist/css/bootstrap.min.css';
import { useAuth } from './AuthContext';
import { useDarkMode } from '../utils/DarkModeProvider';
import { ScaleLoader } from 'react-spinners';
import Swal from 'sweetalert2';
import anexo3Pdf from '../assets/anexo_3.pdf';
import { FaDownload } from 'react-icons/fa';
import {
    FaBuilding,
    FaChalkboardTeacher,
    FaCommentDots,
    FaGlobeAmericas,
    FaGraduationCap,
    FaMicroscope,
    FaPaintBrush,
    FaUniversity,
    FaUsers
} from 'react-icons/fa';

const TITULO_CATEGORIA_PRINCIPAL = 'PUNTAJE DE LAS ACTIVIDADES Y PRODUCTOS DEL PERSONAL - Actividad y producción docente, científica, artística y de gestión.';
const DESCRIPCION_CATEGORIA_PRINCIPAL = 'No deben incluirse actividades realizadas con anterioridad al período considerado. Todos los topes se refieren al período que es objeto de evaluación (4 años). Todos los puntajes son por producto o actividad a menos que se consigne que se trata de un puntaje anual, en cuyo caso se aplicará proporcionalidad para calcular desempeños o tareas por lapsos inferiores en actividades de puntaje anual.';

export default function PanelesCategorias({ onCategoriaSeleccionada, periodoSeleccionado, docenteId }) {
    const { darkMode } = useDarkMode();
    const { token } = useAuth();
    const docenteActualId = docenteId;
    const dynamicStyles = darkMode ? darkModeStyles : lightModeStyles;

    const [categorias, setCategorias] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [categoriaCargandoId, setCategoriaCargandoId] = useState(null);

    useEffect(() => {
        if (!periodoSeleccionado?.id || !docenteActualId || !token) {
            setCategorias(null);
            setLoading(false);
            setError('No se encontró el docente seleccionado para cargar categorías');
            return;
        }

        setLoading(true);
        setError(null);

        fetch(`${process.env.REACT_APP_API_URL}/item-categoria-periodo-padre/instancias/${periodoSeleccionado.id}/${docenteActualId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Error en la solicitud');
                }
                return response.json();
            })
            .then((res) => {
                setCategorias(res);
            })
            .catch((fetchError) => {
                console.log(fetchError);
                setError(fetchError.message);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [periodoSeleccionado?.id, docenteActualId, token]);

    const getCategoriaSeleccionada = (id) => {
        if (!docenteActualId || !periodoSeleccionado?.id || !token) {
            setError('No se encontró el docente seleccionado para cargar la categoría');
            return;
        }

        setCategoriaCargandoId(id);
        setError(null);

        fetch(`${process.env.REACT_APP_API_URL}/item-categoria-periodo-docente/${id}/${docenteActualId}/${periodoSeleccionado.id}/`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
            }
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error('Error en la solicitud');
                }
                return response.json();
            })
            .then((res) => {
                onCategoriaSeleccionada(res);
            })
            .catch((fetchError) => {
                console.log(fetchError);
                setError(fetchError.message);
            })
            .finally(() => {
                setLoading(false);
                setCategoriaCargandoId(null);
            });
    };

    const normalizarClaveCategoria = (texto) => (texto || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toUpperCase()
        .replace(/[^A-Z0-9\s-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const iconosCategorias = {
        [normalizarClaveCategoria('FORMACION ACADEMICA')]: <FaGraduationCap size={40} />,
        [normalizarClaveCategoria('ACTIVIDAD Y PRODUCCION EN DOCENCIA')]: <FaChalkboardTeacher size={40} />,
        [normalizarClaveCategoria('ACTIVIDAD Y PRODUCCION EN INVESTIGACION CIENTIFICA')]: <FaMicroscope size={40} />,
        [normalizarClaveCategoria('EXTENSION, TRANSFERENCIA Y DIVULGACION')]: <FaGlobeAmericas size={40} />,
        [normalizarClaveCategoria('PRODUCCION ARTISTICA')]: <FaPaintBrush size={40} />,
        [normalizarClaveCategoria('FORMACION DE RECURSOS HUMANOS')]: <FaUsers size={40} />,
        [normalizarClaveCategoria('GESTION UNIVERSITARIA')]: <FaUniversity size={40} />,
        [normalizarClaveCategoria('REFERENCIA INSTITUCIONAL')]: <FaBuilding size={40} />,
        [normalizarClaveCategoria('OBSERVACIONES Y COMENTARIOS')]: <FaCommentDots size={40} />
    };

    const iconosPorCategoria = (categoria) => {
        const clave = normalizarClaveCategoria(categoria);
        return iconosCategorias[clave] || <FaGraduationCap size={40} />;
    };

    const categoriasArray = Array.isArray(categorias)
        ? categorias
        : Object.values(categorias || {});

    const categoriaPrincipal = categoriasArray[0] || null;
    const categoriasOtras = categoriasArray.slice(1);

    const esRutaWebAccesible = (ruta) => {
        if (!ruta) return false
        if (/^https?:\/\//i.test(ruta)) return true
        if (ruta.startsWith('/assets/') || ruta.startsWith('/static/') || ruta.startsWith('/public/')) return true
        return false
    }

    const descargarGrilla = async () => {
        const rutaEnv = process.env.REACT_APP_RUTA_ANEXO_3
        const rutaDescarga = esRutaWebAccesible(rutaEnv) ? rutaEnv : anexo3Pdf

        try {
            const link = document.createElement('a')
            link.href = rutaDescarga
            link.download = 'anexo_3.pdf'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        } catch (error) {
            console.log(error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo descargar la grilla.',
                confirmButtonText: 'Cerrar',
                confirmButtonColor: '#972034'
            })
        }
    }

    const renderCategoriaCard = (cat, esPrincipal = false) => {
        const totalInstancias = cat.total_instancias?.total ?? 0;
        const totalParaConfirmar = cat.total_instancias?.total_para_confirmar ?? 0;
        const tienePendientes = totalParaConfirmar > 0;
        const todasConfirmadas = totalInstancias > 0 && totalParaConfirmar === 0;
        const estaCargando = categoriaCargandoId === cat.id;

        const cardBaseStyle = esPrincipal
            ? {
                ...styles.bannerCard,
                ...(darkMode ? styles.bannerCardDark : styles.bannerCardLight)
            }
            : {
                ...styles.card,
                ...(darkMode ? styles.cardDark : styles.cardLight)
            };

        const estadoStyle = tienePendientes
            ? (darkMode ? styles.cardPendienteDark : styles.cardPendienteLight)
            : todasConfirmadas
                ? (darkMode ? styles.cardConfirmadaDark : styles.cardConfirmadaLight)
                : {};

        return (
            <div
                key={cat.id}
                role={esPrincipal ? undefined : 'button'}
                tabIndex={esPrincipal ? undefined : 0}
                aria-disabled={esPrincipal ? undefined : estaCargando}
                style={{
                    ...cardBaseStyle,
                    ...estadoStyle,
                    cursor: esPrincipal ? 'default' : (estaCargando ? 'progress' : 'pointer')
                }}
                onClick={esPrincipal ? undefined : () => {
                    if (!estaCargando) {
                        getCategoriaSeleccionada(cat.id);
                    }
                }}
                onKeyDown={esPrincipal ? undefined : (event) => {
                    if (estaCargando) {
                        return;
                    }

                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        getCategoriaSeleccionada(cat.id);
                    }
                }}
                onMouseEnter={esPrincipal ? undefined : (event) => {
                    if (estaCargando) {
                        return;
                    }

                    event.currentTarget.style.transform = 'translateY(-3px)';
                    event.currentTarget.style.boxShadow = darkMode
                        ? '0 18px 30px rgba(0, 0, 0, 0.34)'
                        : '0 18px 30px rgba(0, 0, 0, 0.14)';
                }}
                onMouseLeave={esPrincipal ? undefined : (event) => {
                    event.currentTarget.style.transform = 'translateY(0)';
                    event.currentTarget.style.boxShadow = cardBaseStyle.boxShadow;
                }}
            >
                <div style={esPrincipal ? styles.bannerContenido : styles.cardContenido}>
                    {!esPrincipal && (
                        <div style={styles.iconoCard}>
                            {iconosPorCategoria(cat.categoria?.nombre)}
                        </div>
                    )}

                    <div style={styles.textoCard}>
                        <h3 style={esPrincipal ? { ...styles.tituloBanner, color: darkMode ? '#fff4f5' : '#7e1f2f' } : { ...styles.tituloCard, color: darkMode ? '#ffffff' : '#1f1f1f' }}>
                            {esPrincipal
                                ? TITULO_CATEGORIA_PRINCIPAL
                                : `${cat.indice_completo}. ${cat.categoria?.nombre}`}
                        </h3>

                        {esPrincipal && (
                            <p style={{ ...styles.descripcionBanner, color: darkMode ? '#f0e5e7' : '#5f5255' }}>
                                {DESCRIPCION_CATEGORIA_PRINCIPAL}
                            </p>
                        )}

                        {!esPrincipal && estaCargando && (
                            <div style={styles.loadingIndicator}>
                                <ScaleLoader color={darkMode ? '#ffffff' : '#972034'} height={18} width={3} margin={2} />
                                <span style={{ ...styles.loadingText, color: darkMode ? '#f2f2f2' : '#4b4b4b' }}>Cargando...</span>
                            </div>
                        )}

                        {!esPrincipal && (
                            <>
                                {tienePendientes && <div style={styles.badgePendiente}>Pendientes de Verificación</div>}
                                {/* {todasConfirmadas && (
                                    <div style={darkMode ? styles.badgeCompletaDark : styles.badgeCompletaLight}>Completada</div>
                                )} */}
                            </>
                        )}

                        {!esPrincipal && (
                            <div style={styles.estadisticasContainer}>
                                <div style={styles.estadisticaLinea}>
                                    <span style={styles.estadisticaLabel}>Total instancias:</span>
                                    <span style={styles.estadisticaValor}>{totalInstancias}</span>
                                </div>
                                <div style={styles.estadisticaLinea}>
                                    <span style={styles.estadisticaLabel}>Por confirmar:</span>
                                    <span style={styles.estadisticaValorPendiente}>{totalParaConfirmar}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={styles.wrapper}>
            <div style={styles.headerRow}>
                <div style={styles.headerTopRow}>
                    <h2 className="pb-2" style={{ ...dynamicStyles.title, fontSize: '1.45rem', marginBottom: 0, paddingBottom: 0, borderBottom: 'none' }}>
                        Categorías
                    </h2>
                    <button
                        type="button"
                        onClick={descargarGrilla}
                        style={{
                            ...styles.downloadButton,
                            ...(darkMode ? styles.downloadButtonDark : styles.downloadButtonLight)
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#a23c41'
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = darkMode
                                ? styles.downloadButtonDark.backgroundColor
                                : styles.downloadButtonLight.backgroundColor
                        }}
                    >
                        <FaDownload style={styles.downloadButtonIcon} />
                        Descargar Grilla
                    </button>
                </div>
                <div style={{ ...styles.titleUnderline, borderBottom: dynamicStyles.title.borderBottom }} />
            </div>

            {loading && !categoriaPrincipal ? (
                <div style={styles.loadingContainer}>
                    <ScaleLoader color={darkMode ? '#ffffff' : '#972034'} height={18} width={3} margin={2} />
                </div>
            ) : (
                <>
                    {error && (
                        <div style={{ ...styles.errorBox, ...(darkMode ? styles.errorBoxDark : styles.errorBoxLight) }}>
                            {error}
                        </div>
                    )}

                    {categoriaPrincipal && renderCategoriaCard(categoriaPrincipal, true)}

                    {categoriasOtras.length > 0 && (
                        <div style={styles.grid}>
                            {categoriasOtras.map((cat) => renderCategoriaCard(cat, false))}
                        </div>
                    )}

                    {!categoriaPrincipal && !error && (
                        <div style={{ ...styles.errorBox, ...(darkMode ? styles.errorBoxDark : styles.errorBoxLight) }}>
                            No se encontraron categorías para mostrar.
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

const lightModeStyles = {
    title: {
        color: '#A23C41',
        borderBottom: '4px solid #646354'
    }
};

const darkModeStyles = {
    title: {
        borderBottom: '4px solid #495057'
    }
};

const styles = {
    wrapper: {
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px 0 28px'
    },
    headerRow: {
        marginBottom: '16px'
    },
    headerTopRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px'
    },
    titleUnderline: {
        marginTop: '4px',
        width: '100%'
    },
    downloadButton: {
        border: 'none',
        color: '#fff',
        padding: '10px 16px',
        borderRadius: '5px',
        fontSize: '16px',
        fontWeight: 'bold',
        cursor: 'pointer',
        transition: 'background 0.3s',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        flexShrink: 0
    },
    downloadButtonLight: {
        backgroundColor: '#972034'
    },
    downloadButtonDark: {
        backgroundColor: '#972034'
    },
    downloadButtonIcon: {
        fontSize: '14px'
    },
    loadingContainer: {
        minHeight: '240px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },
    errorBox: {
        borderRadius: '16px',
        padding: '14px 16px',
        marginBottom: '18px',
        fontWeight: 600,
        lineHeight: 1.45,
        border: '1px solid transparent'
    },
    errorBoxLight: {
        backgroundColor: '#fff4f5',
        color: '#7e1f2f',
        borderColor: '#efc4cb'
    },
    errorBoxDark: {
        backgroundColor: '#312629',
        color: '#f5d8dd',
        borderColor: '#5a444a'
    },
    bannerCard: {
        borderRadius: '18px',
        padding: '12px 16px',
        marginBottom: '10px',
        border: '1px solid transparent',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.10)'
    },
    bannerCardLight: {
        background: 'linear-gradient(135deg, #fff7f8 0%, #fdecef 45%, #f7dde2 100%)',
        borderColor: '#efc4cb'
    },
    bannerCardDark: {
        background: 'linear-gradient(135deg, #24191d 0%, #2f2227 52%, #37272d 100%)',
        borderColor: '#5d454b'
    },
    bannerContenido: {
        display: 'block'
    },
    tituloBanner: {
        margin: 0,
        fontSize: '16px',
        fontWeight: 800,
        lineHeight: 1.35,
        textAlign: 'center'
    },
    descripcionBanner: {
        margin: '6px auto 0',
        fontSize: '13px',
        lineHeight: 1.6,
        maxWidth: '900px',
        textAlign: 'left'
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '18px'
    },
    card: {
        borderRadius: '18px',
        padding: '18px',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '260px',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
        boxShadow: '0 12px 24px rgba(0, 0, 0, 0.10)',
        border: '1px solid transparent'
    },
    cardLight: {
        backgroundColor: '#f4f4f4'
    },
    cardDark: {
        backgroundColor: '#25292d'
    },
    cardContenido: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },
    iconoCard: {
        marginBottom: '10px',
        color: '#972034',
        display: 'flex',
        justifyContent: 'center'
    },
    textoCard: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },
    tituloCard: {
        margin: 0,
        fontSize: '16px',
        fontWeight: 800,
        lineHeight: 1.35,
        minHeight: '52px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center'
    },
    loadingIndicator: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        marginTop: '10px',
        marginBottom: '10px',
        minHeight: '34px'
    },
    loadingText: {
        fontSize: '12px',
        fontWeight: 700,
        letterSpacing: '0.03em'
    },
    badgePendiente: {
        alignSelf: 'center',
        backgroundColor: '#f4b400',
        color: '#1f1f1f',
        borderRadius: '999px',
        padding: '4px 10px',
        fontSize: '12px',
        fontWeight: 700,
        marginBottom: '8px'
    },
    badgeCompletaLight: {
        alignSelf: 'center',
        backgroundColor: '#dff5e8',
        color: '#1c6b41',
        border: '1px solid #a7dfbe',
        borderRadius: '999px',
        padding: '4px 10px',
        fontSize: '12px',
        fontWeight: 700,
        marginBottom: '8px'
    },
    badgeCompletaDark: {
        alignSelf: 'center',
        backgroundColor: '#1f3b2d',
        color: '#8fe0b2',
        border: '1px solid #2f6b4d',
        borderRadius: '999px',
        padding: '4px 10px',
        fontSize: '12px',
        fontWeight: 700,
        marginBottom: '8px'
    },
    estadisticasContainer: {
        marginTop: 'auto',
        paddingTop: '12px',
        borderTop: '1px solid rgba(0,0,0,0.12)',
        textAlign: 'left'
    },
    estadisticaLinea: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        fontSize: '14px',
        marginBottom: '4px'
    },
    estadisticaLabel: {
        fontWeight: 600
    },
    estadisticaValor: {
        fontWeight: 800
    },
    estadisticaValorPendiente: {
        fontWeight: 800,
        color: '#972034'
    }
};
